import axios from 'axios'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
})

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('beebro.token')
  if (t) cfg.headers.Authorization = `Bearer ${t}`
  return cfg
})

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      localStorage.removeItem('beebro.token')
      window.location.href = '/login'
    }
    return Promise.reject(err)
  },
)

export interface PanelUser {
  id: number
  email: string
  role: string
  display_name?: string
  is_active: boolean
}

export interface Node {
  id: string
  name: string
  hostname: string
  ip_address: string
  ssh_user?: string
  ssh_port: number
  status: string
  status_message?: string
  agent_version?: string
  os_name?: string
  kernel?: string
  arch?: string
  cpu_count?: number
  mem_bytes?: number
  disk_bytes?: number
  enabled_features: string[]
  last_heartbeat_at?: string
  created_at: string
}

export interface Client {
  id: number
  node_id: string
  unix_user: string
  unix_uid?: number
  home_dir: string
  full_name?: string
  email?: string
  is_suspended: boolean
  created_at: string
}

export interface Site {
  id: number
  client_id: number
  node_id: string
  domain: string
  aliases: string[]
  docroot: string
  public_docroot?: string
  charset: string
  dir_index: string
  handler: string
  email?: string
  php_enabled: boolean
  php_mode: string
  php_version?: string
  open_basedir: boolean
  semantic_url: boolean
  ssl_enabled: boolean
  ssl_port: number
  ssl_cert_id?: number
  redirect_http_to_https: boolean
  hsts_enabled: boolean
  listen_ips: string[]
  foreground: boolean
  techdomain: boolean
  redirect_www: 'off'|'to_www'|'from_www'
  autosubdomain: 'off'|'dir'|'subdir'
  gzip_enabled: boolean
  gzip_level: number
  cache_enabled: boolean
  cache_ttl_sec: number
  cache_extensions: string
  ssi_enabled: boolean
  ddos_shield: boolean
  ddos_limit_rps: number
  ddos_burst: number
  access_log_enabled: boolean
  error_log_enabled: boolean
  is_suspended: boolean
  created_at: string
}

export interface Database {
  id: number
  client_id: number
  db_server_id: number
  name: string
  charset: string
  collation_name: string
  size_bytes: number
  created_at: string
}

export interface DBUser {
  id: number
  client_id: number
  db_server_id: number
  name: string
  host: string
  created_at: string
}

export interface DBServer {
  id: number
  node_id: string
  type: string
  host: string
  port: number
  version?: string
  admin_user: string
  is_default: boolean
  created_at: string
}

export interface FTPUser {
  id: number
  client_id: number
  node_id: string
  name: string
  home_dir: string
  is_active: boolean
  created_at: string
}

export interface SSLCert {
  id: number
  node_id: string
  name: string
  type: 'letsencrypt' | 'custom' | 'selfsigned'
  issuer?: string
  subject?: string
  domains: string[]
  valid_from?: string
  valid_until?: string
  created_at: string
}

export interface CronJob {
  id: number
  client_id: number
  node_id: string
  command: string
  schedule: string
  is_active: boolean
  notify_email?: string
  created_at: string
}

export interface ActiveTask {
  id: string
  node_id: string
  kind: string
  status: string
  percent: number
  created_at: string
}

export interface NodeInventory {
  nginx: boolean
  nginx_version?: string
  apache: boolean
  mysql: boolean
  mysql_version?: string
  mariadb: boolean
  postfix: boolean
  dovecot: boolean
  sftp: boolean
  sftp_group: boolean
  certbot: boolean
  php_versions: string[]
  docker: boolean
  redis: boolean
  neon: boolean
  nodejs_version?: string
  python3_version?: string
}

export interface LatestMetric {
  node_id: string
  cpu_percent: number
  mem_used: number
  mem_total: number
  disk_used: number
  disk_total: number
  load1: number
  ts: string
}

export interface MetricPoint {
  ts: string
  cpu_percent: number
  mem_used: number
  mem_total: number
  disk_used: number
  disk_total: number
  net_rx: number
  net_tx: number
  load1_x100: number
  io_wait_pct: number
}

export interface Session {
  id: number
  user_id: number
  jti: string
  user_agent?: string
  ip_address?: string
  created_at: string
  expires_at: string
  revoked_at?: string
  last_used_at?: string
  current?: boolean
}

export interface AuditEntry {
  id: number
  user_id?: number
  email: string
  action: string
  ip_address?: string
  user_agent?: string
  details?: string
  ts: string
}

export interface APIKey {
  id: number
  user_id: number
  name: string
  key_prefix: string
  scopes: string[]
  ip_whitelist: string[]
  last_used_at?: string
  expires_at?: string
  created_at: string
  revoked_at?: string
}

export interface Task {
  id: string
  node_id: string
  kind: string
  status: string
  percent: number
  error?: string
  payload?: any
  result?: any
  created_at: string
  started_at?: string
  finished_at?: string
}

export interface TaskLog {
  id: number
  task_id: string
  stream: string
  line: string
  ts: string
}

export const PHP_VERSIONS = ['5.6', '7.4', '8.1', '8.2', '8.3', '8.4']

export function humanBytes(n?: number): string {
  if (!n) return '0'
  const u = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let v = n
  while (v > 1024 && i < u.length - 1) { v /= 1024; i++ }
  return v.toFixed(v >= 100 ? 0 : 1) + ' ' + u[i]
}

export function timeAgo(iso?: string): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const s = Math.floor(diff / 1000)
  if (s < 60) return s + 's ago'
  const m = Math.floor(s / 60)
  if (m < 60) return m + 'm ago'
  const h = Math.floor(m / 60)
  if (h < 24) return h + 'h ago'
  return Math.floor(h / 24) + 'd ago'
}
